// apps/landing-page/src/lib/cart-storage.ts
import Cookies from 'js-cookie';
import { clientCookieAttributes } from './cookies';

export const CART_COOKIE = 'aks_cart';
export const CART_ITEM_CAP = 15;
export const CART_COOKIE_MAX_AGE_DAYS = 30;

export interface CartItem {
  type?: 'module' | 'course' | 'event' | 'bundle';
  moduleId: string; // for modules: testModuleId; for courses: courseId; for events: eventId; for bundles: bundleId
  title: string;
  price: number;
  basePrice: number;
  promoPrice: number | null;
  isFree: boolean;
  coverImageUrl?: string | null;
}

/**
 * Trims a cart item to exactly the fields the drawer renders — keeps the
 * cookie payload small enough that CART_ITEM_CAP (15) items stay safely
 * under the 4KB cookie ceiling (~260-320B/item trimmed).
 */
export function trimCartItem(item: CartItem): CartItem {
  return {
    type: item.type,
    moduleId: item.moduleId,
    title: item.title,
    price: item.price,
    basePrice: item.basePrice,
    promoPrice: item.promoPrice,
    isFree: item.isFree,
    coverImageUrl: item.coverImageUrl ?? null,
  };
}

/**
 * Reads and parses the aks_cart cookie. Self-heals two known-bad shapes:
 * malformed JSON (wipes to empty) and free-event leaks — free live events are
 * register-only and must never sit in the cart; paid event tickets are
 * legitimate and are kept. Paralleled by the same filter in saveCartToCookie
 * so a leak can't re-enter via a stale write from the other origin.
 */
export function loadCartFromCookie(): CartItem[] {
  const raw = Cookies.get(CART_COOKIE);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return (parsed as CartItem[]).filter((item) => !(item?.type === 'event' && item?.isFree));
  } catch {
    return [];
  }
}

/**
 * Persists items to the aks_cart cookie, trimming fields and enforcing the
 * cap defensively (CartContext.addItem also enforces it — this is belt and
 * suspenders against any future caller that bypasses addItem).
 */
export function saveCartToCookie(items: CartItem[]): void {
  const capped = items.slice(0, CART_ITEM_CAP).map(trimCartItem);
  Cookies.set(CART_COOKIE, JSON.stringify(capped), clientCookieAttributes(CART_COOKIE_MAX_AGE_DAYS));
}

/** Whether one more item can be added without exceeding CART_ITEM_CAP. */
export function canAddCartItem(items: CartItem[]): boolean {
  return items.length < CART_ITEM_CAP;
}

/** Module/course/bundle ids the user already owns — see /payment/owned. */
export interface OwnedIds {
  moduleIds: Iterable<string>;
  courseIds: Iterable<string>;
  bundleIds: Iterable<string>;
}

/**
 * Drops cart items the user already owns. Called on every cart hydrate so a
 * module bought in another tab, another device, or via an async gateway
 * (bank transfer confirmed after the user left the success-redirect page)
 * doesn't keep sitting in the cart and inflating the header badge forever.
 * Event tickets are never "owned" in this sense (/payment/owned doesn't
 * cover them) and are left untouched.
 */
export function reconcileCartWithOwnedItems(items: CartItem[], owned: OwnedIds): CartItem[] {
  const moduleIds = new Set(owned.moduleIds);
  const courseIds = new Set(owned.courseIds);
  const bundleIds = new Set(owned.bundleIds);
  return items.filter((item) => {
    if (item.type === 'course') return !courseIds.has(item.moduleId);
    if (item.type === 'bundle') return !bundleIds.has(item.moduleId);
    if (item.type === 'event') return true;
    return !moduleIds.has(item.moduleId);
  });
}
